import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import { IconChevronLeft, IconChevronRight, IconDownload, IconPhoto, IconShare } from '@tabler/icons-react';
import ReCarousel from '../ReCarousel/ReCarousel.jsx';
import { renderMarkdownToReact } from '../MarkdownText/MarkdownText.jsx';
import { fetchShareFile, getCachedShareFile, prefetchShareFile, saveFile } from '../shared/nativeShare.js';
import styles from './SocialShareCarousel.module.scss';

// Produktneutrale Texte. Die Aufrufstelle überschreibt sie einzeln über `labels`;
// Platzhalter in geschweiften Klammern füllt `fill()` mit den Laufzeitwerten.
const DEFAULT_LABELS = {
    section: 'Grafiken für Social Media',
    carouselRole: 'Karussell',
    carouselHint: 'Grafik-Karussell. Mit den Pfeiltasten navigieren.',
    slideRole: 'Folie',
    slidePosition: '{current} von {total}',
    slideAlt: '{placeholder} {current}',
    previous: 'Vorherige Grafik',
    next: 'Nächste Grafik',
    pagination: 'Grafik auswählen',
    dot: 'Grafik {current} anzeigen',
    liveStatus: 'Grafik {current} von {total}',
    share: 'Bild teilen',
    download: 'Grafik herunterladen',
    shareText: 'Jetzt abstimmen!',
    storyPlaceholder: 'Story-Grafik Vorschau',
    feedPlaceholder: 'Feed-Grafik Vorschau',
};

const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);

const FORMATS = {
    story: { placeholderKey: 'storyPlaceholder', cardClass: '' },
    feed: { placeholderKey: 'feedPlaceholder', cardClass: styles.CardFeed },
};

const EMPTY_SLIDE = { id: 'placeholder', url: '', altText: '', fileName: '' };

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const getSlideFileName = (slide, index) => {
    if (slide.fileName) return slide.fileName;
    try {
        const path = new URL(slide.url, window.location.origin).pathname;
        const name = path.split('/').filter(Boolean).pop();
        if (name) return name;
    } catch {
        // Fall back to a generated name for unparsable URLs.
    }
    return `grafik-${index + 1}.jpg`;
};

const openSlideInNewTab = (slide) => {
    window.open(slide.url, '_blank', 'noopener,noreferrer');
};

const Placeholder = ({ label }) => (
    <div className={styles.Placeholder}>
        <IconPhoto size={48} stroke={1.25} aria-hidden="true" />
        <span className={styles.PlaceholderLabel}>{label}</span>
    </div>
);

const SocialShareCarousel = ({
    headline,
    description,
    format = 'story',
    slides,
    shareText,
    shareTitle,
    shareUrl,
    style = {},
    labels,
}) => {
    const meta = FORMATS[format] || FORMATS.story;
    const copy = { ...DEFAULT_LABELS, ...labels };
    const placeholderLabel = copy[meta.placeholderKey];

    const items = useMemo(() => {
        const validSlides = (Array.isArray(slides) ? slides : []).filter((slide) => slide && slide.url);
        return validSlides.length > 0 ? validSlides : [EMPTY_SLIDE];
    }, [slides]);

    const [activeIndex, setActiveIndex] = useState(0);
    const hasMultipleSlides = items.length > 1;
    const currentSlide = items[Math.min(activeIndex, items.length - 1)];
    const slideSignature = items.map((slide) => slide.id || slide.url).join('|');
    const reduceMotion = prefersReducedMotion();
    const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    useEffect(() => {
        setActiveIndex(0);
    }, [slideSignature]);

    // navigator.share() only works while the click's user activation is alive, so the graphic is
    // fetched ahead of time instead of inside the click handler (large graphics outlive it).
    useEffect(() => {
        if (!canNativeShare || !currentSlide?.url) return;

        prefetchShareFile(currentSlide.url, getSlideFileName(currentSlide, activeIndex));
    }, [activeIndex, canNativeShare, currentSlide]);

    const downloadCurrentSlide = useCallback(async () => {
        try {
            const file =
                getCachedShareFile(currentSlide.url) ??
                (await fetchShareFile(currentSlide.url, getSlideFileName(currentSlide, activeIndex)));
            saveFile(file);
        } catch {
            // Cross-origin graphics without CORS headers cannot be fetched; opening them still allows saving.
            openSlideInNewTab(currentSlide);
        }
    }, [activeIndex, currentSlide]);

    const shareCurrentSlide = useCallback(() => {
        if (!canNativeShare) {
            downloadCurrentSlide();
            return;
        }

        const shareData = {
            title: shareTitle || headline || document.title,
            text: shareText || copy.shareText,
            url: shareUrl || window.location.href,
        };
        const file = getCachedShareFile(currentSlide.url);
        const payload = file && navigator.canShare?.({ files: [file] }) ? { ...shareData, files: [file] } : shareData;

        let pending;
        try {
            pending = navigator.share(payload);
        } catch {
            downloadCurrentSlide();
            return;
        }

        pending?.catch((error) => {
            // A cancelled share dialog is not an error the user needs to hear about.
            if (error?.name !== 'AbortError') downloadCurrentSlide();
        });
    }, [canNativeShare, copy.shareText, currentSlide, downloadCurrentSlide, headline, shareText, shareTitle, shareUrl]);

    const goToSlide = useCallback(
        (index) => setActiveIndex(Math.max(0, Math.min(index, items.length - 1))),
        [items.length]
    );

    const handleTransitionEnd = useCallback((index) => {
        setActiveIndex(index);
    }, []);

    const handleKeyDown = (event) => {
        if (!hasMultipleSlides) return;
        const keyActions = {
            ArrowLeft: () => goToSlide(activeIndex - 1),
            ArrowRight: () => goToSlide(activeIndex + 1),
            Home: () => goToSlide(0),
            End: () => goToSlide(items.length - 1),
        };
        const action = keyActions[event.key];
        if (action) {
            event.preventDefault();
            action();
        }
    };

    const frames = items.map((slide, index) => (
        <div
            key={slide.id || index}
            className={styles.SlideFrame}
            role="group"
            aria-roledescription={copy.slideRole}
            aria-label={fill(copy.slidePosition, { current: index + 1, total: items.length })}
            aria-hidden={index !== activeIndex}
        >
            <div className={styles.SlideContent}>
                {slide.url ? (
                    <img
                        className={styles.Image}
                        src={slide.url}
                        alt={
                            slide.altText || fill(copy.slideAlt, { placeholder: placeholderLabel, current: index + 1 })
                        }
                        loading={index === 0 ? 'eager' : 'lazy'}
                        draggable={false}
                    />
                ) : (
                    <Placeholder label={placeholderLabel} />
                )}
            </div>
        </div>
    ));

    if (!currentSlide) return null;

    return (
        <section
            className={styles.SocialShareCarousel}
            style={style}
            aria-roledescription={copy.carouselRole}
            aria-label={headline || copy.section}
        >
            {headline && <h2 className={styles.Headline}>{headline}</h2>}
            {description && <div className={styles.Description}>{renderMarkdownToReact(description)}</div>}

            <div className={[styles.Card, meta.cardClass].filter(Boolean).join(' ')}>
                <div className={styles.MediaArea}>
                    <div
                        className={styles.Viewport}
                        tabIndex={hasMultipleSlides ? 0 : undefined}
                        onKeyDown={handleKeyDown}
                        aria-label={hasMultipleSlides ? copy.carouselHint : undefined}
                    >
                        <ReCarousel
                            axis="x"
                            loop={false}
                            duration={reduceMotion ? 0 : 300}
                            desiredSlide={activeIndex}
                            onStartFrameIndex={0}
                            onTransitionEnd={handleTransitionEnd}
                            className={styles.Carousel}
                        >
                            {frames}
                        </ReCarousel>
                    </div>

                    {hasMultipleSlides && (
                        <>
                            <button
                                type="button"
                                className={`${styles.Arrow} ${styles.ArrowPrev} anl_social-share-carousel--prev`}
                                onClick={() => goToSlide(activeIndex - 1)}
                                disabled={activeIndex === 0}
                                aria-label={copy.previous}
                            >
                                <IconChevronLeft size={24} aria-hidden="true" />
                            </button>
                            <button
                                type="button"
                                className={`${styles.Arrow} ${styles.ArrowNext} anl_social-share-carousel--next`}
                                onClick={() => goToSlide(activeIndex + 1)}
                                disabled={activeIndex === items.length - 1}
                                aria-label={copy.next}
                            >
                                <IconChevronRight size={24} aria-hidden="true" />
                            </button>
                        </>
                    )}
                </div>

                {hasMultipleSlides && (
                    <div className={styles.Pagination} role="group" aria-label={copy.pagination}>
                        {items.map((slide, index) => (
                            <button
                                key={slide.id || index}
                                type="button"
                                className={`${styles.Dot} anl_social-share-carousel--dot-${index + 1}`}
                                onClick={() => goToSlide(index)}
                                aria-label={fill(copy.dot, { current: index + 1 })}
                                aria-current={index === activeIndex ? 'true' : undefined}
                            />
                        ))}
                    </div>
                )}

                {currentSlide.url && (
                    <Button
                        color="yellow"
                        className={`${styles.ActionButton} ${
                            canNativeShare
                                ? 'anl_social-share-carousel--share anl_social-share-carousel--vote'
                                : 'anl_social-share-carousel--download anl_social-share-carousel--vote'
                        }`}
                        onClick={shareCurrentSlide}
                    >
                        <span className={styles.ActionLabel}>
                            {canNativeShare ? (
                                <IconShare size={20} aria-hidden="true" />
                            ) : (
                                <IconDownload size={20} aria-hidden="true" />
                            )}
                            {canNativeShare ? copy.share : copy.download}
                        </span>
                    </Button>
                )}
            </div>

            <div className="visually-hidden" aria-live="polite" aria-atomic="true">
                {fill(copy.liveStatus, { current: activeIndex + 1, total: items.length })}
            </div>
        </section>
    );
};

export default SocialShareCarousel;
